/**
 * Marketing attribution parameters carried across the authentication round trip.
 *
 * These used to be forwarded to Auth0 as `authorizationParams`. They must not
 * be sent to Cognito: they are PixelMe application state, not authorization
 * parameters, and an authorization server is entitled to reject unknown ones.
 * They are stored per-tab by `lib/signupContext.js` instead and attached to
 * the hub onboarding URL after the callback.
 */
export const MARKETING_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'gspk', 'gsxid', 'referer'];

/**
 * Extracts marketing attribution parameters from URL search params
 * @param {URLSearchParams|string} searchParams URLSearchParams object or search string
 * @returns {Record<string, string>} Object containing only the marketing parameters that are present
 */
export function extractMarketingParams(searchParams) {
  const params = typeof searchParams === 'string' ? new URLSearchParams(searchParams) : searchParams;

  return MARKETING_PARAMS.reduce((acc, param) => {
    const values = params.getAll(param);

    if (values.length > 0) {
      acc[param] = values.join(',');
    }

    return acc;
  }, {});
}

/**
 * Gets marketing parameters from the current window location
 * @returns {Record<string, string>} Object containing only the marketing parameters that are present
 */
export function getMarketingParamsFromWindow() {
  if (typeof window === 'undefined') {
    return {};
  }

  const marketingParams = extractMarketingParams(window.location.search);

  // Additionally, check if returnTo param is present and extract marketing params from there
  const urlParams = new URLSearchParams(window.location.search);
  const returnTo = urlParams.get('returnTo');

  if (returnTo) {
    try {
      const returnToUrl = new URL(returnTo, window.location.origin);
      const returnToMarketingParams = extractMarketingParams(returnToUrl.search);

      return {
        ...marketingParams,
        ...returnToMarketingParams,
      };
    } catch {
      // Invalid returnTo URL, ignore
    }
  }

  return marketingParams;
}
